/**
 * Sequence Scroll — scroll-driven canvas + fly-through cards
 * Horizon Theme v3.4.0
 *
 * Faithfully replicates the More Nutrition Matcha sequence behavior:
 *   - Canvas frame scrub with scale-down at end
 *   - Cards fly vertically through viewport with rotation + stagger
 *   - SVG lines draw in then draw out
 *   - Inner signature fades in then out
 *
 * Two media modes:
 *   "sequence" — canvas renders frame images from CDN
 *   "static"  — Shopify-hosted images crossfade on scroll
 *
 * APIs: IntersectionObserver, requestAnimationFrame, ResizeObserver, requestIdleCallback
 * No GSAP · No jQuery · No ScrollMagic
 */
function initSequenceScroll() {
    'use strict';

    /* ---- Guard: reduced-motion only (mobile now uses same scroll animation) ---- */
    var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    /* ---- Utilities ---- */
    function clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }
    function lerp(a, b, t) { return a + (b - a) * t; }
    var rIC = window.requestIdleCallback || function (cb) { return setTimeout(cb, 1); };
    var rAF = window.requestAnimationFrame;

    /* ---- DOM refs ---- */
    var section = document.querySelector('[data-sequence]');
    if (!section) return;

    var trigger = section.querySelector('[data-sequence-trigger]');
    var stage = section.querySelector('[data-sequence-stage]');
    var cards = section.querySelectorAll('[data-sequence-card]');
    var svgPaths = section.querySelectorAll('[data-sequence-svg] path');
    var smileys = section.querySelectorAll('[data-sequence-smiley]');
    var innerSig = section.querySelector('[data-sequence-signature]');
    var finalWrap = section.querySelector('.sequence-final');
    var scrollTitle = section.querySelector('[data-sequence-scroll-title]');

    if (!trigger || !stage) return;

    /* ---- Detect mode ---- */
    var mode = section.getAttribute('data-sequence-mode') || 'sequence';

    /* ================================================================
       CARD FLY-THROUGH ANIMATION (matching original GSAP behavior)
       Cards start below viewport, fly upward past the screen.
       Each card is staggered. Left cards get slight positive xPercent,
       right cards get slight negative xPercent, with rotation.
       ================================================================ */
    var vh = window.innerHeight;
    var isMobileView = window.matchMedia('(max-width: 767px)').matches;
    /* On mobile: sequential cards (no overlap). Window = stagger so one ends before next starts */
    var CARD_STAGGER_OFFSET = isMobileView ? 0.22 : 0.18;
    var CARD_WINDOW        = isMobileView ? 0.22 : 0.35;
    var CARD_TRAVEL = 1.2 * vh;

    var cardConfigs = [];
    cards.forEach(function (card, i) {
        var isLeft = card.hasAttribute('data-sequence-card-left');
        var startProgress, endProgress;
        if (isMobileView) {
            if (i === 0) {
                // Card 1 starts visible and centered
                startProgress = -0.12;
                endProgress = 0.12;
            } else if (i === 1) {
                startProgress = 0.20;
                endProgress = 0.42;
            } else if (i === 2) {
                startProgress = 0.44;
                endProgress = 0.66;
            } else {
                startProgress = 0.68;
                endProgress = 0.90;
            }
        } else {
            startProgress = 0.02 + i * CARD_STAGGER_OFFSET;
            endProgress = startProgress + CARD_WINDOW;
        }
        var rotation = isLeft ? -(2 + Math.random() * 3) : (2 + Math.random() * 3);
        cardConfigs.push({
            el: card,
            isLeft: isLeft,
            start: startProgress,
            end: endProgress,
            rotation: rotation
        });
        /* Set initial position */
        var initPrefix = isMobileView ? 'translateX(-50%) ' : '';
        if (isMobileView && i === 0) {
            card.style.transform = initPrefix + 'translateY(0px) rotate(0deg)';
            card.style.opacity = '1';
        } else {
            card.style.transform = initPrefix + 'translateY(' + (0.6 * vh) + 'px) rotate(0deg)';
            card.style.opacity = '0';
        }
    });

    /* Smiley rotation targets */
    var smileyTargets = [];
    smileys.forEach(function (s) {
        var isLeft = s.hasAttribute('data-sequence-smiley-left');
        var angle = isLeft ? (8 + Math.random() * 5) : -(8 + Math.random() * 5);
        smileyTargets.push({ el: s, angle: angle });
    });

    /* Custom ease approximating the original cubic bezier */
    function cardEase(t) {
        /* Attempt to mimic: M0,0 C0,0.201 0.098,0.459 0.5,0.5 0.904,0.541 1,0.805 1,1 */
        /* S-curve: slow start, accelerate through middle, slow end */
        return t < 0.5
            ? 2 * t * t
            : 1 - Math.pow(-2 * t + 2, 2) / 2;
    }

    function updateCards(progress) {
        for (var i = 0; i < cardConfigs.length; i++) {
            var cfg = cardConfigs[i];
            var t = clamp((progress - cfg.start) / (cfg.end - cfg.start), 0, 1);
            var eased = cardEase(t);

            /* Y: from +0.6*vh (below) to -0.6*vh (above) */
            var y = lerp(0.6 * vh, -0.6 * vh, eased);
            /* Rotation: from 0 to target */
            var rot = lerp(0, cfg.rotation, eased);
            /* Opacity: fade in at start, fade out near end */
            var opacity = 1;
            if (t < 0.1) opacity = t / 0.1;
            if (t > 0.85) opacity = (1 - t) / 0.15;

            /* On mobile prepend translateX(-50%) so centering via left:50% is preserved */
            var prefix = isMobileView ? 'translateX(-50%) ' : '';
            cfg.el.style.transform = prefix + 'translateY(' + y.toFixed(1) + 'px) rotate(' + rot.toFixed(2) + 'deg)';
            cfg.el.style.opacity = opacity.toFixed(3);
        }
    }

    function updateSmileys(progress) {
        for (var i = 0; i < smileyTargets.length; i++) {
            var s = smileyTargets[i];
            var t = clamp(progress * 1.2, 0, 1);
            var angle = lerp(0, s.angle, t);
            s.el.style.transform = 'rotate(' + angle.toFixed(2) + 'deg)';
        }
    }

    /* ================================================================
       SVG DRAW-IN / DRAW-OUT (matching original GSAP drawSVG behavior)
       Phase 1: line draws in (offset goes from total → 0)
       Phase 2: line draws out from start (dasharray shifts)
       ================================================================ */
    var dashTotals = [];
    svgPaths.forEach(function (path) {
        try {
            var len = path.getTotalLength();
            dashTotals.push(len);
            path.style.strokeDasharray = len;
            path.style.strokeDashoffset = len;
            path.parentElement.style.setProperty('--seq-dash-total', len);
        } catch (e) { dashTotals.push(5000); }
    });

    var SVG_STAGGER = 0.25; /* progress gap between each SVG path */

    /* Each SVG gets its own time window — only 1 visible at a time */
    var svgWindows = [
        { start: 0.00, end: 0.50 },  /* Line 1: first half */
        { start: 0.40, end: 0.90 }   /* Line 2: second half (slight overlap for smooth transition) */
    ];

    function updateSVG(progress) {
        svgPaths.forEach(function (path, i) {
            var len = dashTotals[i] || 5000;
            var win = svgWindows[i] || svgWindows[0];
            var winLen = win.end - win.start;

            /* Local progress within this line's window */
            var localP = clamp((progress - win.start) / winLen, 0, 1);

            /* Fade parent SVG element in/out so only 1 is visible at a time */
            var svgEl = path.parentElement;
            if (svgEl) {
                if (localP <= 0 || localP >= 1) {
                    svgEl.style.opacity = '0';
                } else if (localP < 0.1) {
                    svgEl.style.opacity = (localP / 0.1).toFixed(3);
                } else if (localP > 0.9) {
                    svgEl.style.opacity = ((1 - localP) / 0.1).toFixed(3);
                } else {
                    svgEl.style.opacity = '1';
                }
            }

            if (localP <= 0.5) {
                /* Phase 1: draw in — offset goes from len to 0 */
                var drawIn = localP / 0.5; /* 0→1 */
                path.style.strokeDasharray = len;
                path.style.strokeDashoffset = len * (1 - drawIn);
            } else {
                /* Phase 2: draw out — line disappears from the start */
                var drawOut = (localP - 0.5) / 0.5; /* 0→1 */
                path.style.strokeDasharray = len;
                path.style.strokeDashoffset = -(len * drawOut);
            }
        });
    }

    /* ================================================================
       CANVAS SCALE-DOWN + MOVE DOWN at end of scroll
       Image slides down to sit between the two flavour images
       Also: entrance animation from bottom at the very start
       ================================================================ */
    function updateCanvasScale(progress) {
        if (!stage) return;
        var canvasEl = stage.querySelector('[data-sequence-canvas]') || stage.querySelector('.sequence-scroll-images');
        if (!canvasEl) return;

        var baseTransform = canvasEl.classList.contains('sequence-canvas') ? 'translate(-50%, -50%) ' : '';

        var entranceStart = isMobileView ? 0.04 : 0.0;
        var entranceEnd   = isMobileView ? 0.18 : 0.03;
        var scaleStart    = isMobileView ? 0.80 : 0.75;

        if (progress < entranceStart) {
            canvasEl.style.opacity = '0';
        } else if (progress < entranceEnd) {
            /* Entrance: slide up from below with fade */
            var enter  = (progress - entranceStart) / (entranceEnd - entranceStart); /* 0→1 */
            var enterY = lerp(30, 0, enter);
            var enterOp = clamp(enter, 0, 1);
            canvasEl.style.transform = baseTransform + 'scale(1) translateY(' + enterY.toFixed(1) + '%)';
            canvasEl.style.opacity = enterOp.toFixed(3);
        } else if (progress < scaleStart) {
            canvasEl.style.transform = baseTransform + 'scale(1) translateY(0)';
            canvasEl.style.opacity = '1';
        } else {
            var t = (progress - scaleStart) / (1 - scaleStart);
            var scale = lerp(1, 0.70, t);
            var yShift = lerp(0, 42, t);
            canvasEl.style.transform = baseTransform + 'scale(' + scale.toFixed(4) + ') translateY(' + yShift.toFixed(2) + '%)';
            canvasEl.style.opacity = '1';
        }
    }

    /* ================================================================
       MODE: STATIC — crossfade between Shopify-hosted images
       ================================================================ */
    var staticImages;
    var activeStaticIdx = -1;

    function initStatic() {
        staticImages = section.querySelectorAll('[data-scroll-image]');
        if (!staticImages.length) return;
        staticImages[0].classList.add('is-active');
        activeStaticIdx = 0;
    }

    function updateStatic(progress) {
        if (!staticImages || !staticImages.length) return;
        var count = staticImages.length;
        var idx = Math.min(Math.floor(progress * count), count - 1);
        if (idx !== activeStaticIdx) {
            if (activeStaticIdx >= 0 && activeStaticIdx < count) {
                staticImages[activeStaticIdx].classList.remove('is-active');
            }
            staticImages[idx].classList.add('is-active');
            activeStaticIdx = idx;
        }
    }

    /* ================================================================
       MODE: SEQUENCE — canvas renders frame images from CDN
       ================================================================ */
    var canvas, ctx, frames, totalFrames, loadedCount, firstFrameReady, lastFrameIdx;

    function initSequence() {
        canvas = section.querySelector('[data-sequence-canvas]');
        if (!canvas) return;
        ctx = canvas.getContext('2d');

        var basePath = canvas.getAttribute('data-sequence-canvas-img-path') || '';
        var prefix = canvas.getAttribute('data-frame-prefix') || 'frame_';
        var ext = canvas.getAttribute('data-frame-ext') || '.webp';
        var padLength = parseInt(canvas.getAttribute('data-frame-pad'), 10) || 0;
        var startIndex = parseInt(canvas.getAttribute('data-frame-start'), 10) || 0;
        totalFrames = parseInt(canvas.getAttribute('data-total-frames'), 10) || 200;

        frames = new Array(totalFrames);
        loadedCount = 0;
        firstFrameReady = false;
        lastFrameIdx = -1;

        function buildSrc(index) {
            var num = String(startIndex + index);
            if (padLength > 0) {
                while (num.length < padLength) num = '0' + num;
            }
            return basePath + prefix + num + ext;
        }

        canvas._buildSrc = buildSrc;
        sizeCanvas();
        loadFrames(buildSrc);
    }

    function sizeCanvas() {
        if (!canvas) return;
        var dpr = Math.min(window.devicePixelRatio || 1, 2);
        var rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.scale(dpr, dpr);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
    }

    function drawFrame(idx) {
        if (!canvas) return;
        idx = clamp(idx, 0, totalFrames - 1);
        var img = frames[idx];
        if (!img || !img.complete || !img.naturalWidth) return;

        var dpr = Math.min(window.devicePixelRatio || 1, 2);
        var cw = canvas.width / dpr;
        var ch = canvas.height / dpr;

        var iw = img.naturalWidth;
        var ih = img.naturalHeight;
        var ratio = iw / ih;

        /* Contain-fit (like original) */
        var dw, dh, dx, dy;
        dh = ch;
        dw = ratio * dh;
        dx = Math.round((cw - dw) / 2);
        dy = 0;

        ctx.clearRect(0, 0, cw, ch);
        ctx.drawImage(img, dx, dy, Math.round(dw), Math.round(dh));
    }

    function loadFrame(index, buildSrc) {
        return new Promise(function (resolve) {
            if (frames[index] && frames[index].complete) { resolve(); return; }
            var img = new Image();
            img.decoding = 'async';
            img.src = buildSrc(index);
            img.onload = function () {
                loadedCount++;
                if (index === 0 && !firstFrameReady) {
                    firstFrameReady = true;
                    sizeCanvas();
                    drawFrame(0);
                }
                resolve();
            };
            img.onerror = function () {
                /* Frame failed — skip gracefully */
                resolve();
            };
            frames[index] = img;
        });
    }

    function loadFrames(buildSrc) {
        var immediateCount = Math.min(10, totalFrames);
        var promises = [];
        for (var i = 0; i < immediateCount; i++) {
            promises.push(loadFrame(i, buildSrc));
        }
        Promise.all(promises).then(function () {
            var idx = immediateCount;
            function loadNext(deadline) {
                var batch = 0;
                while (idx < totalFrames && batch < 5) {
                    loadFrame(idx, buildSrc);
                    idx++;
                    batch++;
                    if (typeof deadline !== 'undefined' && deadline.timeRemaining && deadline.timeRemaining() < 2) break;
                }
                if (idx < totalFrames) rIC(loadNext);
            }
            rIC(loadNext);
        });
    }

    /* ================================================================
       INNER SIGNATURE — fade in at 55%, fade out at 90% (like original)
       ================================================================ */
    function updateInnerSignature(progress) {
        if (!innerSig) return;
        if (progress >= 0.55 && progress <= 0.90) {
            /* Fade in between 0.55→0.65, full between 0.65→0.80, fade out 0.80→0.90 */
            var opacity = 1;
            if (progress < 0.65) opacity = (progress - 0.55) / 0.10;
            if (progress > 0.80) opacity = (0.90 - progress) / 0.10;
            innerSig.style.opacity = clamp(opacity, 0, 1).toFixed(3);
        } else {
            innerSig.style.opacity = '0';
        }
    }

    /* ================================================================
       SCROLL TITLE — heading slides up from below, BEHIND the product
       Appears during the last 30% of scroll (0.70 → 1.0)
       ================================================================ */
    function updateScrollTitle(progress) {
        if (!scrollTitle) return;
        if (progress < 0.70) {
            scrollTitle.style.transform = 'translate(-50%, 100%)';
            scrollTitle.style.opacity = '0';
        } else {
            var t = (progress - 0.70) / 0.30; /* 0→1 */
            var yShift = lerp(100, -150, t); /* from 100% below to -150% above center — fills top whitespace */
            var op = clamp(t / 0.3, 0, 1); /* fade in over first 30% of the reveal */
            scrollTitle.style.transform = 'translate(-50%, ' + yShift.toFixed(1) + '%)';
            scrollTitle.style.opacity = op.toFixed(3);
        }
    }

    /* ================================================================
       SCROLL HANDLER — drives all animations from scroll progress
       ================================================================ */
    var ticking = false;

    function onScroll() {
        if (ticking) return;
        ticking = true;
        rAF(function () {
            var rect = trigger.getBoundingClientRect();
            var scrollHeight = trigger.offsetHeight - window.innerHeight;
            if (scrollHeight <= 0) { ticking = false; return; }
            var scrolled = -rect.top;
            var progress = clamp(scrolled / scrollHeight, 0, 1);

            /* Media update */
            if (mode === 'static') {
                updateStatic(progress);
            } else if (canvas) {
                var frameIdx = Math.round(progress * (totalFrames - 1));
                if (frameIdx !== lastFrameIdx) {
                    drawFrame(frameIdx);
                    lastFrameIdx = frameIdx;
                }
            }

            /* Canvas / images scale-down */
            updateCanvasScale(progress);

            /* Cards fly-through */
            updateCards(progress);

            /* Smiley rotation */
            updateSmileys(progress);

            /* SVG draw-in/draw-out */
            updateSVG(progress);

            /* Inner signature */
            updateInnerSignature(progress);

            /* Scroll title (behind product) */
            updateScrollTitle(progress);

            ticking = false;
        });
    }

    /* ---- Visibility lifecycle ---- */
    var scrollBound = false;
    function attachScroll() {
        if (!scrollBound) {
            window.addEventListener('scroll', onScroll, { passive: true });
            scrollBound = true;
            onScroll();
        }
    }
    function detachScroll() {
        window.removeEventListener('scroll', onScroll);
        scrollBound = false;
    }

    new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
            e.isIntersecting ? attachScroll() : detachScroll();
        });
    }, { rootMargin: '200px' }).observe(trigger);

    /* ---- Memory lifecycle (sequence mode only) ---- */
    if (mode === 'sequence') {
        new IntersectionObserver(function (entries) {
            entries.forEach(function (e) {
                if (!e.isIntersecting && frames) {
                    for (var i = 0; i < frames.length; i++) frames[i] = null;
                    loadedCount = 0;
                    firstFrameReady = false;
                    lastFrameIdx = -1;
                } else if (e.isIntersecting && frames && loadedCount === 0 && canvas && canvas._buildSrc) {
                    loadFrames(canvas._buildSrc);
                }
            });
        }, { rootMargin: '600px' }).observe(section);
    }

    /* ---- Final reveal ---- */
    if (finalWrap) {
        new IntersectionObserver(function (entries) {
            entries.forEach(function (e) {
                if (e.isIntersecting) finalWrap.classList.add('is-visible');
            });
        }, { threshold: 0.25 }).observe(finalWrap);
    }

    /* ---- Resize handling ---- */
    function onResize() {
        vh = window.innerHeight;
        CARD_TRAVEL = 1.2 * vh;
        isMobileView = window.matchMedia('(max-width: 767px)').matches;
        if (mode === 'sequence' && canvas) {
            sizeCanvas();
            if (lastFrameIdx >= 0) drawFrame(lastFrameIdx);
        }
    }

    if (mode === 'sequence' && canvas) {
        new ResizeObserver(function () { onResize(); }).observe(canvas);
    } else {
        window.addEventListener('resize', onResize, { passive: true });
    }

    /* ---- Init ---- */
    if (mode === 'static') {
        initStatic();
    } else {
        initSequence();
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSequenceScroll);
} else {
    initSequenceScroll();
}
